import { useQuery } from "@tanstack/react-query";
import { Building, ChevronDown, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CheckIcon } from "@heroicons/react/24/outline";

import useBreakpoints from "@/hooks/useBreakpoints";

import CareIcon from "@/CAREUI/icons/CareIcon";
import query from "@/Utils/request/query";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Organization } from "@/types/organization/organization";
import organizationApi from "@/types/organization/organizationApi";

interface RoleOrgSelectorProps {
  value?: string[] | null;
  onChange: (value: string[] | null) => void;
  currentOrganizations?: Organization[];
  singleSelection?: boolean;
  optional?: boolean;
}

export default function RoleOrgSelector(props: RoleOrgSelectorProps) {
  const { t } = useTranslation();
  const { onChange, currentOrganizations, singleSelection = false } = props;

  const [selectedOrganizations, setSelectedOrganizations] = useState<
    Organization[]
  >([]);
  const [currentSelection, setCurrentSelection] = useState<Organization | null>(
    null,
  );
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [alreadySelected, setAlreadySelected] = useState(false);
  const isMobile = useBreakpoints({ default: true, sm: false });

  const {
    data: availableOrganizations,
    isLoading: isLoadingAvailableOrganizations,
  } = useQuery({
    queryKey: ["organizations", orgSearchQuery],
    queryFn: query(organizationApi.list, {
      queryParams: {
        org_type: "role",
        name: orgSearchQuery || undefined,
      },
    }),
  });

  const handleSelect = (org: Organization) => {
    const isAlreadySelected = !!currentOrganizations?.find(
      (o) => o.id === org.id,
    );
    if (isAlreadySelected) {
      setAlreadySelected(true);
      setCurrentSelection(org);
      setOrgSearchQuery("");
      return;
    }
    handleConfirmSelection(org);
    setCurrentSelection(org);
    setOrgSearchQuery("");
  };

  const handleConfirmSelection = useCallback(
    (org: Organization) => {
      if (!selectedOrganizations.includes(org)) {
        const newSelection = [...selectedOrganizations, org];
        setSelectedOrganizations(newSelection);
        onChange(newSelection.map((org) => org.id));
        setAlreadySelected(true);
      }
      setCurrentSelection(null);
      setOpen(false);
    },
    [selectedOrganizations, onChange],
  );

  const handleRemoveOrganization = (index: number) => {
    const newSelection = selectedOrganizations.filter((_, i) => i !== index);
    setSelectedOrganizations(newSelection);
    onChange(
      newSelection.length > 0 ? newSelection.map((org) => org.id) : null,
    );
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setOrgSearchQuery("");
    }
  };

  // Auto-select when there's only one organization available
  useEffect(() => {
    const availableOrgs = availableOrganizations?.results || [];

    // Only auto-select if:
    // 1. There's exactly one organization
    // 2. No search is active
    // 3. No organizations are currently selected
    // 4. Not loading
    if (
      availableOrgs.length === 1 &&
      !orgSearchQuery &&
      selectedOrganizations.length === 0 &&
      !isLoadingAvailableOrganizations
    ) {
      const singleOrg = availableOrgs[0];

      // Check if this organization is already selected in currentOrganizations prop
      const isAlreadyInCurrent = currentOrganizations?.find(
        (org) => org.id === singleOrg.id,
      );

      if (!isAlreadyInCurrent && !props.optional) {
        handleConfirmSelection(singleOrg);
      }
    }
  }, [
    availableOrganizations,
    handleConfirmSelection,
    orgSearchQuery,
    selectedOrganizations,
    isLoadingAvailableOrganizations,
    currentOrganizations,
    props.optional,
  ]);

  const renderOrganizationCommand = (className?: string) => {
    return (
      <Command className={className}>
        <div className="flex flex-col px-3 py-2 border-b sticky top-0 bg-white z-10">
          <span className="font-semibold text-base text-gray-900">
            {t("select_organization")}
          </span>
          <span className="text-sm text-gray-500 mt-0.5">
            {t("select_organization_description")}
          </span>
        </div>
        <div className="flex items-center border-b px-3 sticky top-[48px] bg-white z-10">
          <CommandInput
            placeholder={t("search_organizations")}
            onValueChange={setOrgSearchQuery}
            value={orgSearchQuery}
            className="border-none focus:ring-0 text-base sm:text-sm"
          />
        </div>
        <CommandList onWheel={(e) => e.stopPropagation()}>
          <CommandEmpty>
            {isLoadingAvailableOrganizations ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                <span className="ml-2 text-sm text-gray-500">
                  {t("loading_organizations")}
                </span>
              </div>
            ) : (
              t("no_organizations_found")
            )}
          </CommandEmpty>
          <CommandGroup>
            {!isLoadingAvailableOrganizations &&
              (availableOrganizations?.results || []).map((org) => {
                const isSelected = currentSelection?.id === org.id;
                return (
                  <CommandItem
                    key={org.id}
                    value={org.name}
                    onSelect={() => handleSelect(org)}
                    className={cn(
                      "flex items-center justify-between",
                      isSelected && "bg-sky-50/50",
                    )}
                  >
                    <div className="flex items-center">
                      <span>{org.name}</span>
                      {isSelected && (
                        <CheckIcon className="ml-2 h-4 w-4 text-sky-600" />
                      )}
                    </div>
                  </CommandItem>
                );
              })}
          </CommandGroup>
        </CommandList>
        {currentSelection && (
          <div className="md:m-0 m-2 flex items-center justify-between px-3 py-2 bg-sky-50/50 border-sky-200 rounded-md">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-0.5">
                {t("selected")}
              </span>
              <span className="font-medium text-sm text-sky-900">
                {currentSelection.name}
              </span>
            </div>
            {alreadySelected ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-2"
                disabled={alreadySelected}
              >
                <span>{t("already_selected")}</span>
                <CareIcon icon="l-multiply" className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-2"
                onClick={() => handleConfirmSelection(currentSelection)}
                disabled={isDisabled}
              >
                <span>{t("confirm")}</span>
                <CheckIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </Command>
    );
  };

  const isDisabled = useMemo(() => {
    return (
      selectedOrganizations.some((org) => org.id === currentSelection?.id) ||
      (!!currentOrganizations &&
        currentOrganizations.some((org) => org.id === currentSelection?.id))
    );
  }, [currentSelection, currentOrganizations, selectedOrganizations]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <Label>
            {t("select_organization")}
            {!props.optional && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-3">
          <div className="flex flex-col gap-2">
            {selectedOrganizations.map((org, index) => (
              <div
                key={index}
                className="flex-1 flex items-center gap-3 rounded-md border border-sky-100 bg-sky-50/50 p-2.5"
              >
                <Building className="size-4 text-sky-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-sky-900 truncate">
                    {org.name}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 p-0 text-gray-500 hover:text-gray-900"
                  onClick={() => handleRemoveOrganization(index)}
                >
                  <X className="size-4" />
                  <span className="sr-only">{t("remove_organization")}</span>
                </Button>
              </div>
            ))}
            {(!singleSelection ||
              (singleSelection && selectedOrganizations.length < 1)) &&
              (isMobile ? (
                <>
                  <Drawer open={open} onOpenChange={setOpen}>
                    <DrawerTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between border-dashed"
                        onClick={() => setOpen(true)}
                        type="button" // Prevents unintended form submission
                      >
                        <span className="truncate text-gray-500">
                          {currentSelection
                            ? currentSelection.name
                            : t("select_organization")}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="min-h-[50vh] max-h-[85vh]">
                      {renderOrganizationCommand()}
                    </DrawerContent>
                  </Drawer>
                </>
              ) : (
                <Popover open={open} onOpenChange={handleOpenChange}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className="w-full justify-between border-dashed"
                    >
                      <span className="truncate text-gray-500">
                        {currentSelection
                          ? currentSelection.name
                          : t("select_organization")}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={4}
                    className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[80vh] overflow-auto"
                  >
                    {renderOrganizationCommand()}
                  </PopoverContent>
                </Popover>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
